import React, { useEffect, useState } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import SellingSpot from '../../../models/selling/SellingSpot';
import { Coordinate } from '../../../models/tariff/trafficAreas/Coordinate';
import { GOOGLE_MAPS_API_KEY, SPLIT_COORDINATE, USE_LEAFLET_MAPS } from '../../../config';
import MapSpotsLeaflet from './MapSpotsLeaflet';

const containerStyle = {
    width: '100%',
    height: '260px',
    border: '1px solid transparent',
    borderRadius: '15px'
};

const containerStyleFullView = {
    width: '100%',
    height: '520px'
};

interface Props {
    sellingSpots: SellingSpot[];
    activeMarker?: Coordinate;
    fullView?: boolean;
    onActiveMarkerChange?: (cooridnate?: Coordinate) => any;
}

function MapSpots(props: Props) {
    if (USE_LEAFLET_MAPS)
        return <MapSpotsLeaflet {...props} />;

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: GOOGLE_MAPS_API_KEY
    });

    const [map, setMap] = useState<google.maps.Map | undefined>(undefined);

    const onLoad = React.useCallback((googleMap: google.maps.Map) => {
        setMap(googleMap);
    }, []);

    const onUnmount = React.useCallback(() => {
        setMap(undefined);
    }, []);

    useEffect(() => {
        const coordinate = props.activeMarker || { latitude: SPLIT_COORDINATE.lat, longitude: SPLIT_COORDINATE.lng };
        const zoom = props.activeMarker ? 17 : 12.9;

        centerMap(coordinate, zoom);
    }, [map, props.activeMarker]);

    const centerMap = (coordinate: Coordinate, zoom: number) => {
        if (!map)
            return;

        const center = { lat: coordinate.latitude, lng: coordinate.longitude };

        map.setCenter(center);
        map.setZoom(zoom);
        map.panTo(center);
    };

    const getMarkerContent = (sellingSpot: SellingSpot) => (
        <>
            <h4>{sellingSpot.name}</h4>
            <h6 className='mb-3'>{sellingSpot.fullAddress}</h6>
            {[...new Set(sellingSpot.workingHours)].map((workingHours, i) => <p key={i}>{workingHours}</p>)}
        </>
    );

    const onMarkerClick = (coordinate?: Coordinate) => {
        if (!props.onActiveMarkerChange)
            return;

        props.onActiveMarkerChange(coordinate);
    };

    if (!isLoaded)
        return <></>;

    return (
        <GoogleMap
            mapContainerStyle={props.fullView ? containerStyleFullView : containerStyle}
            onLoad={onLoad}
            onUnmount={onUnmount}
        >
            {props.sellingSpots.map((sellingSpot) => {
                const markerKey = `${sellingSpot.coordinate.latitude}-${sellingSpot.coordinate.longitude}`;
                const isActiveMarker =
                    props.activeMarker?.latitude === sellingSpot.coordinate.latitude
                    && props.activeMarker?.longitude === sellingSpot.coordinate.longitude;

                return (
                    <Marker
                        key={markerKey}
                        position={{
                            lat: sellingSpot.coordinate.latitude,
                            lng: sellingSpot.coordinate.longitude
                        }}
                        onClick={() => onMarkerClick(sellingSpot.coordinate)}
                    >
                        {isActiveMarker && (
                            <InfoWindow onCloseClick={() => onMarkerClick(undefined)}>
                                {getMarkerContent(sellingSpot)}
                            </InfoWindow>
                        )}
                    </Marker>
                );
            })}
        </GoogleMap>
    );
}

export default React.memo(MapSpots);
